import { Accordion, AccordionSummary } from "@mui/material";
import React, { useMemo } from "react";
import { BiChevronDown } from "react-icons/bi";
import { BudgetItemTypeEnum } from "../../../../types/budget/budget";
import { EventScopeEnum } from "../../../../types/project/enums";
import {
  useBudgetMeta,
  useBudgetSuppliersState,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useProject } from "../../../../contexts/project.context";
import CategoryAccordionSummary from "./CategoryAccordionSummary";
import {
  CATEGORY_ROW_BACKGROUND,
  categoryAccordionSx,
  categoryAccordionTransitionProps,
} from "./categoryAccordionUi";
import { useCategoryTotals } from "./useCategoryTotals";

type Props = {
  type: keyof typeof BudgetItemTypeEnum;
  supplierId: string;
  panel: string;
  index: number;
  onChange: (
    panel: string,
  ) => (_event: React.SyntheticEvent, isExpanded: boolean) => void;
  onMoveBillingModel: (type: keyof typeof BudgetItemTypeEnum) => void;
  onTransferSupplier: (type: keyof typeof BudgetItemTypeEnum) => void;
  onRemoveItem: (type: keyof typeof BudgetItemTypeEnum) => void;
};

const CategoryAccordionHeader: React.FC<Props> = ({
  type,
  supplierId,
  panel,
  index,
  onChange,
  onMoveBillingModel,
  onTransferSupplier,
  onRemoveItem,
}) => {
  const { activeCalcOption, versionInfo, isBudgetDisabled } = useBudgetMeta();
  const { baseValues } = useBudgetSuppliersState();
  const { tabActive, event } = useProject();
  const { calcTotalsSource, calcTotalsCompare } = useCategoryTotals(
    supplierId,
    type,
  );

  const baseValue = baseValues?.find(
    (el) =>
      el.calcOption === activeCalcOption &&
      el.supplierId === supplierId &&
      el.category === type &&
      el.version === versionInfo.version,
  );

  const baseValueExchangeCurrency = useMemo(() => {
    if (event?.eventScope === EventScopeEnum.NATIONAL) {
      return "BRL";
    }
    return baseValue?.exchange?.currency || "BRL";
  }, [event?.eventScope, baseValue?.exchange?.currency]);

  return (
    <Accordion
      sx={categoryAccordionSx}
      expanded={false}
      onChange={onChange(panel)}
      TransitionProps={categoryAccordionTransitionProps}
    >
      <AccordionSummary
        aria-controls={`${panel}-content`}
        expandIcon={<BiChevronDown color="white" />}
        id={`${panel}-header`}
        style={{
          backgroundColor: CATEGORY_ROW_BACKGROUND,
          borderBottom: "1px solid #eee",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <CategoryAccordionSummary
          type={type}
          index={index}
          totals={calcTotalsSource}
          comparison={calcTotalsCompare}
          tabActive={tabActive}
          baseValueExchangeCurrency={baseValueExchangeCurrency}
          isBudgetDisabled={isBudgetDisabled}
          onMoveBillingModel={onMoveBillingModel}
          onTransferSupplier={onTransferSupplier}
          onRemoveItem={onRemoveItem}
        />
      </AccordionSummary>
    </Accordion>
  );
};

export default React.memo(CategoryAccordionHeader);
